import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiChevronUp, FiCheck, FiExternalLink, FiCalculator, FiStar, FiArrowRight } = FiIcons;

const InteractivePricing = () => {
  const [isAdvanced, setIsAdvanced] = useState(false);
  const [applyCredits, setApplyCredits] = useState(true);

  // Simple mode states
  const [voiceMinutes, setVoiceMinutes] = useState(1000);
  const [chatMessages, setChatMessages] = useState(10000);

  // Advanced mode states
  const [voiceEngine, setVoiceEngine] = useState('elevenlabs'); // elevenlabs, openai
  const [llmChoice, setLlmChoice] = useState('gpt-4o');
  const [fastTier, setFastTier] = useState(false);
  const [telephonyProvider, setTelephonyProvider] = useState('retell-twilio');
  const [telephonyCountry, setTelephonyCountry] = useState('us');
  const [customTelephony, setCustomTelephony] = useState(false);
  const [knowledgeBaseAddon, setKnowledgeBaseAddon] = useState(false);
  const [batchCalls, setBatchCalls] = useState(0);
  const [brandedCalls, setBrandedCalls] = useState(0);
  const [chatLlm, setChatLlm] = useState('gpt-4o');

  // Monthly subscriptions
  const [phoneNumbers, setPhoneNumbers] = useState(0);
  const [smsService, setSmsService] = useState(0);
  const [concurrency, setConcurrency] = useState(0);
  const [knowledgeBases, setKnowledgeBases] = useState(0);
  const [verifiedNumbers, setVerifiedNumbers] = useState(0);

  // Voice engine rates
  const voiceEngineRates = {
    elevenlabs: 0.07,
    openai: 0.08
  };

  // LLM rates (per minute for call agents)
  const llmRates = {
    'gpt-5': { standard: 0.04, fast: 0.08 },
    'gpt-5-mini': { standard: 0.012, fast: 0.024 },
    'gpt-5-nano': { standard: 0.003, fast: 0.006 },
    'gpt-4.1': { standard: 0.045, fast: 0.0675 },
    'gpt-4.1-mini': { standard: 0.016, fast: 0.024 },
    'gpt-4.1-nano': { standard: 0.004, fast: 0.006 },
    'gpt-4o': { standard: 0.05, fast: 0.075 },
    'gpt-4o-mini': { standard: 0.006, fast: 0.009 },
    'claude-3.7-sonnet': { standard: 0.06, fast: null },
    'claude-3.5-haiku': { standard: 0.02, fast: null },
    'gemini-2.0-flash': { standard: 0.006, fast: null },
    'gemini-2.0-flash-lite': { standard: 0.003, fast: null }
  };

  // Chat LLM rates (per message)
  const chatLlmRates = {
    'gpt-4.1': 0.015,
    'gpt-4.1-mini': 0.006,
    'gpt-4.1-nano': 0.002,
    'gpt-4o': 0.017,
    'gpt-4o-mini': 0.002,
    'claude-3.7-sonnet': 0.021,
    'claude-3.5-haiku': 0.006,
    'gemini-2.0-flash': 0.006,
    'gemini-2.0-flash-lite': 0.003
  };

  // Telephony rates
  const telephonyRates = {
    'retell-twilio': {
      us: 0.015,
      'us-tollfree': 0.06,
      india: 0.15,
      australia: 0.10,
      germany: 0.10,
      spain: 0.10,
      uk: 0.10,
      mexico: 0.05,
      france: 0.06,
      japan: 0.28,
      canada: 0.03,
      italy: 0.06,
      indonesia: 0.40,
      philippines: 0.80,
      malaysia: 0.20,
      thailand: 0.45
    },
    'telnyx': {
      us: 0.03,
      india: 0.25,
      canada: 0.03
    }
  };

  // Monthly subscription rates
  const subscriptionRates = {
    phoneNumbers: 2.00,
    sms: 20.00,
    concurrency: 8.00,
    knowledgeBase: 8.00,
    verifiedNumber: 100.00
  };

  const scrollToGetStarted = () => {
    const element = document.querySelector('[data-section="get-started"]');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const calculateCosts = () => {
    let voiceEngineRate = isAdvanced ? voiceEngineRates[voiceEngine] : 0.07;
    let llmRate = isAdvanced ? (fastTier && llmRates[llmChoice].fast ? llmRates[llmChoice].fast : llmRates[llmChoice].standard) : 0.05; // Default GPT-4o standard rate
    
    let telephonyRate = 0;
    if (!customTelephony && isAdvanced) {
      const provider = telephonyRates[telephonyProvider];
      telephonyRate = provider ? provider[telephonyCountry] || 0.015 : 0.015;
    } else if (!isAdvanced) {
      telephonyRate = 0.015; // Default Retell Twilio US rate
    }
    
    let chatRate = isAdvanced ? chatLlmRates[chatLlm] : 0.002;

    // Voice costs
    const voiceEngineCost = voiceMinutes * voiceEngineRate;
    const llmCost = voiceMinutes * llmRate;
    const telephonyCost = customTelephony ? 0 : voiceMinutes * telephonyRate;

    // Add-ons
    const knowledgeBaseCost = (isAdvanced && knowledgeBaseAddon) ? voiceMinutes * 0.005 : 0;
    const batchCallCost = (isAdvanced && batchCalls > 0) ? batchCalls * 0.005 : 0;
    const brandedCallCost = (isAdvanced && brandedCalls > 0) ? brandedCalls * 0.10 : 0;

    // Chat costs
    const chatCost = chatMessages * chatRate;

    // Monthly subscriptions
    const subscriptionCost = isAdvanced ? (
      phoneNumbers * subscriptionRates.phoneNumbers +
      smsService * subscriptionRates.sms +
      concurrency * subscriptionRates.concurrency +
      knowledgeBases * subscriptionRates.knowledgeBase +
      verifiedNumbers * subscriptionRates.verifiedNumber
    ) : 0;

    const subtotal = voiceEngineCost + llmCost + telephonyCost + knowledgeBaseCost + batchCallCost + brandedCallCost + chatCost + subscriptionCost;
    const creditsApplied = applyCredits ? Math.min(10, subtotal) : 0;
    const total = Math.max(0, subtotal - creditsApplied);

    return {
      voiceEngine: voiceEngineCost,
      llm: llmCost,
      telephony: telephonyCost,
      knowledgeBase: knowledgeBaseCost,
      batchCall: batchCallCost,
      brandedCall: brandedCallCost,
      chat: chatCost,
      subscriptions: subscriptionCost,
      subtotal,
      creditsApplied,
      total
    };
  };

  const costs = calculateCosts();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Transparent Usage-Based Pricing
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Pay only for what you use. No hidden fees, no surprise charges.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Retell Pay as you go Plan */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Pay as you go</h3>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                Self-Serve
              </span>
            </div>
            <div className="text-center mb-4">
              <div className="text-3xl font-bold text-gray-900 mb-2">Retell</div>
              <div className="text-lg text-gray-600">$0 to start</div>
            </div>
            <ul className="space-y-3 mb-8">
              {[
                '$0.07+/minute for AI Voice Agents',
                '$0.002+/msg for AI Chat Agents',
                'Pre-build Functions',
                'Simulation Testing',
                'Analytics',
                '$10 Free credits',
                '20 Free concurrent calls',
                '10 Free Knowledge Bases',
                'Discord and Email support'
              ].map((feature, index) => (
                <li key={index} className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
            <a
              href="http://crclnk.net/AiVoiceRT"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors duration-300 mb-4 inline-block text-center"
            >
              Start instantly
            </a>
            <p className="text-sm text-gray-600 mb-2">
              Final cost depends on selected voices/LLMs and usage.
            </p>
            <a
              href="https://www.retellai.com/pricing?via=CRCNU"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 transition-colors"
            >
              See deeper breakdowns
              <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
            </a>
          </motion.div>

          {/* Enterprise Plan */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 border border-purple-200 relative"
          >
            {/* Popular Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
                <SafeIcon icon={FiStar} className="w-4 h-4" />
                Most Popular
              </div>
            </div>
            <div className="flex items-center gap-3 mb-4 mt-4">
              <h3 className="text-2xl font-bold text-gray-900">Enterprise</h3>
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                Premium
              </span>
            </div>
            <div className="text-center mb-4">
              <div className="text-3xl font-bold text-gray-900 mb-2">Custom Solution</div>
              <div className="text-lg text-gray-600">Custom Pricing</div>
            </div>
            <ul className="space-y-3 mb-8">
              {[
                'Advanced AI Voice Technology',
                'Custom Voice Training',
                'Enterprise-Grade Security',
                'Dedicated Account Manager',
                'Priority Support (24/7)',
                'Custom Integrations',
                'Advanced Analytics & Reporting',
                'White-Label Solutions',
                'SLA Guarantees',
                'Unlimited Customization'
              ].map((feature, index) => (
                <li key={index} className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-purple-500 flex-shrink-0" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={scrollToGetStarted}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 mb-4 inline-flex items-center justify-center gap-2"
            >
              Request a Quote
              <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
            </button>
            <p className="text-sm text-gray-600 text-center">
              Tailored solutions for enterprise-level requirements with dedicated support and custom features.
            </p>
          </motion.div>

          {/* Interactive Cost Calculator */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl border border-gray-200 shadow-lg"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiCalculator} className="w-5 h-5 text-blue-600" />
                  <h3 className="text-xl font-bold text-gray-900">Cost Calculator</h3>
                </div>
                <button
                  onClick={() => setIsAdvanced(!isAdvanced)}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <span className="text-sm font-medium">
                    {isAdvanced ? 'Simple' : 'Advanced'}
                  </span>
                  <SafeIcon icon={isAdvanced ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Simple Mode Controls */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Voice minutes
                    <span className="text-gray-500 font-normal ml-2">$0.07+/min base</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100000"
                    step="10"
                    value={voiceMinutes}
                    onChange={(e) => setVoiceMinutes(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <input
                    type="number"
                    value={voiceMinutes}
                    onChange={(e) => setVoiceMinutes(Math.max(0, Number(e.target.value)))}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chat messages
                    <span className="text-gray-500 font-normal ml-2">$0.002+/msg base</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1000000"
                    step="100"
                    value={chatMessages}
                    onChange={(e) => setChatMessages(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <input
                    type="number"
                    value={chatMessages}
                    onChange={(e) => setChatMessages(Math.max(0, Number(e.target.value)))}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Apply $10 free credits</label>
                  <button
                    onClick={() => setApplyCredits(!applyCredits)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      applyCredits ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        applyCredits ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Advanced Mode Controls */}
              <AnimatePresence>
                {isAdvanced && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6 border-t border-gray-200 pt-6"
                  >
                    {/* Voice Engine Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Conversation Voice Engine (per minute)
                      </label>
                      <select
                        value={voiceEngine}
                        onChange={(e) => setVoiceEngine(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="elevenlabs">Elevenlabs/Cartesia voices → $0.07/min</option>
                        <option value="openai">OpenAI voices → $0.08/min</option>
                      </select>
                    </div>

                    {/* LLM Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        LLM for Call Agent (per minute)
                      </label>
                      <select
                        value={llmChoice}
                        onChange={(e) => setLlmChoice(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                      >
                        <option value="gpt-5">GPT-5: $0.04/min (Fast $0.08/min)</option>
                        <option value="gpt-5-mini">GPT-5 mini: $0.012/min (Fast $0.024/min)</option>
                        <option value="gpt-5-nano">GPT-5 nano: $0.003/min (Fast $0.006/min)</option>
                        <option value="gpt-4.1">GPT-4.1: $0.045/min (Fast $0.0675/min)</option>
                        <option value="gpt-4.1-mini">GPT-4.1 mini: $0.016/min (Fast $0.024/min)</option>
                        <option value="gpt-4.1-nano">GPT-4.1 nano: $0.004/min (Fast $0.006/min)</option>
                        <option value="gpt-4o">GPT-4o: $0.05/min (Fast $0.075/min)</option>
                        <option value="gpt-4o-mini">GPT-4o mini: $0.006/min (Fast $0.009/min)</option>
                        <option value="claude-3.7-sonnet">Claude 3.7 sonnet: $0.06/min</option>
                        <option value="claude-3.5-haiku">Claude 3.5 haiku: $0.02/min</option>
                        <option value="gemini-2.0-flash">Gemini 2.0 Flash: $0.006/min</option>
                        <option value="gemini-2.0-flash-lite">Gemini 2.0 Flash Lite: $0.003/min</option>
                      </select>
                      {llmRates[llmChoice]?.fast && (
                        <div className="flex items-center justify-between">
                          <label className="text-sm text-gray-600">Fast Tier</label>
                          <button
                            onClick={() => setFastTier(!fastTier)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              fastTier ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                fastTier ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Telephony Section */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telephony (per minute)
                      </label>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-sm text-gray-600">Custom Telephony (BYO)</label>
                          <button
                            onClick={() => setCustomTelephony(!customTelephony)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              customTelephony ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                customTelephony ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        {customTelephony && (
                          <p className="text-sm text-blue-600">
                            Bring your own telephony; telephony charges handled outside this estimate.
                          </p>
                        )}
                        {!customTelephony && (
                          <>
                            <select
                              value={telephonyProvider}
                              onChange={(e) => setTelephonyProvider(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="retell-twilio">Retell Twilio</option>
                              <option value="telnyx">Telnyx</option>
                            </select>
                            <select
                              value={telephonyCountry}
                              onChange={(e) => setTelephonyCountry(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {telephonyProvider === 'retell-twilio' ? (
                                <>
                                  <option value="us">US $0.015</option>
                                  <option value="us-tollfree">US Toll-free $0.06</option>
                                  <option value="india">India $0.15</option>
                                  <option value="australia">Australia $0.10</option>
                                  <option value="germany">Germany $0.10</option>
                                  <option value="spain">Spain $0.10</option>
                                  <option value="uk">UK $0.10</option>
                                  <option value="mexico">Mexico $0.05</option>
                                  <option value="france">France $0.06</option>
                                  <option value="japan">Japan $0.28</option>
                                  <option value="canada">Canada $0.03</option>
                                  <option value="italy">Italy $0.06</option>
                                  <option value="indonesia">Indonesia $0.40</option>
                                  <option value="philippines">Philippines $0.80</option>
                                  <option value="malaysia">Malaysia $0.20</option>
                                  <option value="thailand">Thailand $0.45</option>
                                </>
                              ) : (
                                <>
                                  <option value="us">US $0.03</option>
                                  <option value="india">India $0.25</option>
                                  <option value="canada">Canada $0.03</option>
                                </>
                              )}
                            </select>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Call Agent Add-ons */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Call Agent Add-ons
                      </label>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-sm text-gray-600">Knowledge Base (+$0.005/min)</label>
                          <button
                            onClick={() => setKnowledgeBaseAddon(!knowledgeBaseAddon)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              knowledgeBaseAddon ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                knowledgeBaseAddon ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Batch Call (+$0.005/dial)
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={batchCalls}
                            onChange={(e) => setBatchCalls(Math.max(0, Number(e.target.value)))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Number of dials"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Branded Call (+$0.10/outbound call)
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={brandedCalls}
                            onChange={(e) => setBrandedCalls(Math.max(0, Number(e.target.value)))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Number of outbound calls"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Chat Agent LLM */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chat Agent LLM (per AI message)
                      </label>
                      <select
                        value={chatLlm}
                        onChange={(e) => setChatLlm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="gpt-4.1">GPT-4.1 — $0.015/msg (Recommended)</option>
                        <option value="gpt-4.1-mini">GPT-4.1 mini — $0.006/msg (Cheaper Choice)</option>
                        <option value="gpt-4.1-nano">GPT-4.1 nano — $0.002/msg</option>
                        <option value="gpt-4o">GPT-4o — $0.017/msg (Recommended)</option>
                        <option value="gpt-4o-mini">GPT-4o mini — $0.002/msg</option>
                        <option value="claude-3.7-sonnet">Claude 3.7 sonnet — $0.021/msg</option>
                        <option value="claude-3.5-haiku">Claude 3.5 haiku — $0.006/msg</option>
                        <option value="gemini-2.0-flash">Gemini 2.0 Flash — $0.006/msg (First 20 concurrency free)</option>
                        <option value="gemini-2.0-flash-lite">Gemini 2.0 Flash Lite — $0.003/msg (First 10 knowledge bases free)</option>
                      </select>
                    </div>

                    {/* Monthly Subscriptions */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Monthly Subscriptions (optional)
                      </label>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Retell Phone Numbers — $2.00/month
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={phoneNumbers}
                            onChange={(e) => setPhoneNumbers(Math.max(0, Number(e.target.value)))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Retell SMS — $20.00/month
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={smsService}
                            onChange={(e) => setSmsService(Math.max(0, Number(e.target.value)))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Concurrency (Active Calls) — $8.00/concurrency/month (first 20 free)
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={concurrency}
                            onChange={(e) => setConcurrency(Math.max(0, Number(e.target.value)))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Knowledge Base — $8.00/KB/month (first 10 free)
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={knowledgeBases}
                            onChange={(e) => setKnowledgeBases(Math.max(0, Number(e.target.value)))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Verified Phone Number — $100.00/number/month (first 10 KBs free)
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={verifiedNumbers}
                            onChange={(e) => setVerifiedNumbers(Math.max(0, Number(e.target.value)))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Cost Breakdown */}
              <div className="bg-gray-50 rounded-lg p-4 border-t border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">Cost Breakdown</h4>
                <div className="space-y-2 text-sm">
                  {costs.voiceEngine > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Voice Engine</span>
                      <span className="font-medium">{formatCurrency(costs.voiceEngine)}</span>
                    </div>
                  )}
                  {costs.llm > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">LLM</span>
                      <span className="font-medium">{formatCurrency(costs.llm)}</span>
                    </div>
                  )}
                  {costs.telephony > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Telephony</span>
                      <span className="font-medium">{formatCurrency(costs.telephony)}</span>
                    </div>
                  )}
                  {costs.knowledgeBase > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Knowledge Base Add-on</span>
                      <span className="font-medium">{formatCurrency(costs.knowledgeBase)}</span>
                    </div>
                  )}
                  {costs.batchCall > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Batch Calls</span>
                      <span className="font-medium">{formatCurrency(costs.batchCall)}</span>
                    </div>
                  )}
                  {costs.brandedCall > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Branded Calls</span>
                      <span className="font-medium">{formatCurrency(costs.brandedCall)}</span>
                    </div>
                  )}
                  {costs.chat > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Chat</span>
                      <span className="font-medium">{formatCurrency(costs.chat)}</span>
                    </div>
                  )}
                  {costs.subscriptions > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monthly Subscriptions</span>
                      <span className="font-medium">{formatCurrency(costs.subscriptions)}</span>
                    </div>
                  )}
                  {costs.creditsApplied > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Free Credits Applied</span>
                      <span className="font-medium">-{formatCurrency(costs.creditsApplied)}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-300 pt-2 flex justify-between text-lg font-bold">
                    <span>Estimated Monthly Total</span>
                    <span>{formatCurrency(costs.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Helper Notes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 text-center text-sm text-gray-600 space-y-2"
        >
          <p>• Prompts exceeding 3,500 tokens will incur a surcharge.</p>
          <p>• Prices shown are starting rates and may vary by voice, LLM, or telephony region.</p>
          <p>• Taxes and carrier fees may apply.</p>
          <p>• This is an estimate, not an invoice.</p>
          <p>
            • For deeper breakdowns, see{' '}
            <a
              href="https://www.retellai.com/pricing?via=CRCNU"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 transition-colors"
            >
              Retell AI's pricing reference
            </a>.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default InteractivePricing;